import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const AiJobsContext = createContext(null);
export const useAiJobs = () => useContext(AiJobsContext);

const LS_KEY = "ai_jobs_pending";
const MAX_AGE_MS = 30 * 60 * 1000;

const readPending = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; } catch { return []; }
};
const writePending = (list) => localStorage.setItem(LS_KEY, JSON.stringify(list));
const addPending = (entry) => writePending([...readPending().filter((e) => e.jobId !== entry.jobId), entry]);
const removePending = (jobId) => writePending(readPending().filter((e) => e.jobId !== jobId));

export function AiJobsProvider({ children }) {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const locRef = useRef(location.pathname);
  locRef.current = location.pathname;
  const resumedRef = useRef(false);

  const notifyDone = useCallback((label, link) => {
    toast.success(`${label}: pronto`, {
      duration: 10000,
      action: link ? { label: "Apri", onClick: () => navigate(link) } : undefined,
    });
  }, [navigate]);

  const poll = useCallback(async ({ jobId, label, link, interval = 2500, deadline }) => {
    setJobs((j) => [...j.filter((x) => x.jobId !== jobId), { jobId, label, link }]);
    const isAway = () => !link || locRef.current !== link;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, interval));
        const { data: job } = await api.get(`/ai-jobs/${jobId}`);
        if (job.status === "done") {
          if (isAway()) notifyDone(label, link);
          return job.result;
        }
        if (job.status === "error") {
          if (isAway()) toast.error(`${label}: generazione non riuscita`);
          throw new Error(job.error || "Generazione AI non riuscita");
        }
        if (Date.now() > deadline) {
          if (isAway()) toast.error(`${label}: timeout della generazione`);
          throw new Error("Timeout: la generazione AI sta impiegando troppo tempo. Riprova.");
        }
      }
    } finally {
      removePending(jobId);
      setJobs((j) => j.filter((x) => x.jobId !== jobId));
    }
  }, [notifyDone]);

  const trackJob = useCallback(async (startPromise, { label = "Generazione AI", link = null, interval = 2500, timeout = 300000 } = {}) => {
    const { data } = await startPromise;
    const jobId = data.job_id;
    if (!jobId) return data;
    addPending({ jobId, label, link, started_at: Date.now() });
    return poll({ jobId, label, link, interval, deadline: Date.now() + timeout });
  }, [poll]);

  useEffect(() => {
    if (!user || user.is_platform_admin || resumedRef.current) return;
    resumedRef.current = true;
    const now = Date.now();
    for (const e of readPending()) {
      if (!e.jobId || now - (e.started_at || 0) > MAX_AGE_MS) { removePending(e.jobId); continue; }
      api.get(`/ai-jobs/${e.jobId}`).then(({ data: job }) => {
        if (job.status === "done") { notifyDone(e.label, e.link); removePending(e.jobId); }
        else if (job.status === "error") { toast.error(`${e.label}: generazione non riuscita`); removePending(e.jobId); }
        else poll({ jobId: e.jobId, label: e.label, link: e.link, deadline: (e.started_at || now) + MAX_AGE_MS }).catch(() => {});
      }).catch(() => removePending(e.jobId));
    }
  }, [user, poll, notifyDone]);

  return (
    <AiJobsContext.Provider value={{ trackJob, activeJobs: jobs }}>
      {children}
    </AiJobsContext.Provider>
  );
}
